import { ColumnDropdown } from '@/tableDefinitionEditor/columnRules/ColumnDropdown.atom';
import type { ColumnDefinition } from '@/tableDefinitionEditor/tableDefinition.types';
import type { ColumnTypeEnum } from '@seeqdev/api/model/ColumnDefinitionInputV1';

interface SelectColumnFromTablePureProps {
  columns: ColumnDefinition[];
  selectedColumnId?: string;
  onChange: (column?: ColumnDefinition) => void;
  allowedColumnTypes?: ColumnTypeEnum[];
  label?: string;
  testId?: string;
  isClearable?: boolean;
  isSearchable?: boolean;
  showError?: boolean;
  allowedColumnsFilter?: (column: ColumnDefinition) => boolean;
  disabled?: boolean;
}

/**
 * Pure, prop-based column selector that doesn't depend on flux stores.
 * Use this when you have columns from an external source (e.g., Vantage scaling tables).
 * For store-based selection from table editors, use SelectColumnFromTable instead.
 */
export const SelectColumnFromTablePure = ({
  columns,
  selectedColumnId,
  onChange,
  allowedColumnTypes,
  label,
  testId = 'selectColumnFromTable',
  isClearable = true,
  isSearchable = true,
  showError = false,
  allowedColumnsFilter = (_column) => true,
  disabled = false,
}: SelectColumnFromTablePureProps) => {
  // Apply filters: allowedColumnTypes and allowedColumnsFilter
  // Note: For pure usage (e.g., scaling tables from Vantage), we include ALL columns
  // including those with isHidden=true, as the caller controls what columns to pass
  let filteredColumns: ColumnDefinition[];
  if (!allowedColumnTypes || allowedColumnTypes.length === 0) {
    filteredColumns = columns.filter(allowedColumnsFilter);
  } else {
    filteredColumns = columns.filter((col) => allowedColumnTypes.includes(col.columnType)).filter(allowedColumnsFilter);
  }

  const dropdownOptions = filteredColumns.map((col) => ({
    id: col.id,
    value: col.columnName,
    text: col.displayName,
  }));

  const displayValueForSelectedColumn = dropdownOptions.find((option) => option.id === selectedColumnId)?.text;

  return (
    <ColumnDropdown
      testId={testId}
      dropdownOptions={dropdownOptions}
      selectOption={(selected) => {
        const potentialColumn = filteredColumns.find((col) => col.columnName === selected);
        onChange(potentialColumn);
      }}
      label={label || ''}
      currentDropdownValue={displayValueForSelectedColumn}
      isClearable={isClearable}
      isSearchable={isSearchable}
      showError={showError}
      disabled={disabled}
    />
  );
};
